import { useEffect, useRef, useMemo } from "react";
import { TextNodeType } from "../../CommonLandingMweb.types";
import { Carousel, Container, Item, ItemInner, LeftIcon, Title } from "./Styles";
import FormattedText from "../../../FormattedText/FormattedText";

interface CarouselItem {
  title?: TextNodeType;
  subtitle?: TextNodeType;
  leftIcon?: string;
  rightIcon?: string;
  cta?: { title?: TextNodeType };
}

interface CarouselPersuasionProps {
  carouselData?: CarouselItem[];
}

const AUTO_SCROLL_INTERVAL = 3000;

export const CarouselPersuasion = ({ carouselData = [] }: CarouselPersuasionProps) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const currentIndex = useRef(0);
  const totalReal = carouselData.length;

  const items = useMemo(() => {
    if (totalReal <= 1) return carouselData;
    return [...carouselData, carouselData[0]];
  }, [carouselData, totalReal]);

  useEffect(() => {
    if (totalReal <= 1) return;

    let resetTimeout: ReturnType<typeof setTimeout> | undefined;

    const timer = setInterval(() => {
      const el = carouselRef.current;
      if (!el) return;

      currentIndex.current += 1;

      if (currentIndex.current <= totalReal) {
        el.scrollTo({ left: el.clientWidth * currentIndex.current });
      }

      if (currentIndex.current === totalReal) {
        resetTimeout = setTimeout(() => {
          if (!carouselRef.current) return;
          carouselRef.current.style.scrollBehavior = "auto";
          carouselRef.current.scrollTo({ left: 0 });
          carouselRef.current.style.scrollBehavior = "smooth";
          currentIndex.current = 0;
        }, 500);
      }
    }, AUTO_SCROLL_INTERVAL);

    return () => {
      clearInterval(timer);
      if (resetTimeout) clearTimeout(resetTimeout);
    };
  }, [totalReal]);

  if (!carouselData.length) return null;

  return (
    <Container>
      <Carousel ref={carouselRef}>
        {items.map((item, idx) => (
          <Item key={`cp-${idx}`}>
            <ItemInner>
              {item.leftIcon ? <LeftIcon src={item.leftIcon} alt="" /> : null}
              <Title>
                <FormattedText value={item.title} />
              </Title>
            </ItemInner>
          </Item>
        ))}
      </Carousel>
    </Container>
  );
};
